/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	Truck,
	Package,
	Clock,
	AlertTriangle,
	Mail,
	MapPin,
	CalendarDays,
	HelpCircle,
	DollarSign,
	Info,
} from "lucide-react"; // Refined icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path
import ReactMarkdown from "react-markdown";

// --- Structured Shipping Information ---
// Separating content for easier updates and clarity
const shippingData = {
	processingTime: {
		icon: Clock,
		title: "Order Processing Time",
		content: [
			"We take pride in our fast turnaround! Your custom shades are crafted locally in Toronto and are typically ready to ship within **2 business days**.",
			"Please note: Business days exclude weekends and Canadian public holidays. Processing time is separate from shipping transit time.",
		],
	},
	locationsCosts: {
		icon: MapPin,
		title: "Shipping Locations & Costs",
		sections: [
			{
				subtitle: "Canada",
				icon: <span className="fi fi-ca mr-2"></span>, // Using flag icons (requires setup) or text
				points: [
					"Enjoy **FREE Standard Shipping** on all orders across Canada, including remote locations.",
					"We primarily use reliable carriers like Canada Post and Canpar.",
					"Sales tax is calculated at checkout based on your province/territory.",
				],
			},
			{
				subtitle: "United States",
				icon: <span className="fi fi-us mr-2"></span>, // Flag icon
				points: [
					"Shipping costs to the USA are calculated automatically during checkout.",
					"Costs are based on the order's size, weight, and your specific delivery address.",
					"**Important:** Any applicable duties, taxes, or customs brokerage fees upon import are the **responsibility of the customer**.",
				],
			},
			{
				subtitle: "International",
				icon: HelpCircle, // Example, replace if needed
				points: [
					"Currently, we only ship directly to Canada and the USA.",
					<>
						If you are located outside these areas, please{" "}
						<a
							href="mailto:info@offcutshades.com"
							className="text-primary hover:underline font-medium"
						>
							email us at info@offcutshades.com
						</a>
						, and we'll do our best to assist.
					</>, // Use standard mailto
				],
			},
		],
	},
	deliveryTimes: {
		icon: CalendarDays,
		title: "Estimated Delivery Times",
		content: [
			"Delivery time estimates begin **after** your order has shipped (following the ~2 business day processing time).",
			"**Canada (Standard):** Typically 3-9 business days.",
			"**USA (Standard):** Typically 5-10 business days (may vary by location and customs).",
			"Estimates are subject to carrier performance, delivery location, weather, and order details. We do not ship on weekends or holidays.",
			"Your order may arrive in multiple shipments depending on the items.",
		],
	},
	damagedItems: {
		icon: AlertTriangle,
		title: "Damaged Items in Transport",
		content: [
			"**Please inspect your package immediately upon arrival.**",
			<>
				If you notice any damage to the packaging or the product itself, please
				contact us at{" "}
				<a
					href="mailto:info@offcutshades.com"
					className="text-primary hover:underline font-medium"
				>
					info@offcutshades.com
				</a>{" "}
				within **seven (7) days** of receiving your order.
			</>,
			"**Include photos!** Clear pictures of the damaged packaging *and* the damaged product are essential for processing your claim quickly.",
			"Unfortunately, claims for shipping damage reported after the 7-day window cannot be accepted.",
		],
	},
};

// --- Main Shipping Policy Screen Component ---
const ShippingPolicyScreen = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			{" "}
			{/* Use standard bg */}
			<Helmet>
				<title>Shipping Information & Policy | Off Cut Shades</title>{" "}
				{/* SEO Title */}
				<meta
					name="description"
					content="Learn about Off Cut Shades shipping policy. We offer FREE Canada-wide shipping and fast 2-day processing from Toronto. Get details on US shipping, delivery times, and damage claims."
				/>{" "}
				{/* SEO Desc */}
				<link
					rel="canonical"
					href="https://offcutshades.com/help/shipping-policy"
				/>
			</Helmet>
			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<Truck size={36} strokeWidth={1.5} /> {/* Lucide Icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Shipping Information
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="text-lg text-gray-600 max-w-3xl mx-auto"
					>
						Fast processing, clear policies. Find out how we get your custom
						shades from our Toronto factory to your door.
					</motion.p>
				</section>
				{/* --- Policy Sections --- */}
				<section className="container mx-auto px-4 max-w-4xl mx-auto">
					<div className="space-y-8 md:space-y-10">
						{/* Map over structured data */}
						{Object.values(shippingData).map((section, index) => (
							<motion.div
								key={section.title}
								className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
							>
								<div className="flex flex-col sm:flex-row items-start gap-4 mb-4">
									<div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
										{React.createElement(section.icon, {
											size: 24,
											strokeWidth: 2,
										})}
									</div>
									<h2 className="text-xl md:text-2xl font-semibold text-gray-800 mt-1">
										{section.title}
									</h2>
								</div>
								<div className="space-y-3 text-gray-700 text-sm sm:text-base pl-0 sm:pl-14">
									{" "}
									{/* Indent content */}
									{section.intro && <p className="mb-3">{section.intro}</p>}
									{/* Handle content array or sections array */}
									{Array.isArray(section.content)
										? section.content.map((paragraph, pIndex) => (
												<div key={pIndex}>
													<ReactMarkdown>{paragraph}</ReactMarkdown>
												</div>
										  ))
										: section.sections
										? section.sections.map((subSection) => (
												<div
													key={subSection.subtitle}
													className="pt-3 border-t border-gray-100 first:border-t-0 first:pt-0"
												>
													<h3 className="font-semibold text-base mb-2 flex items-center">
														{/* {subSection.icon} // Render flag icon if set up */}
														{subSection.subtitle}
													</h3>
													<ul className="space-y-1.5 list-disc list-outside pl-5">
														{subSection.points.map(
															(point, ptIndex) =>
																typeof point === "string" ? (
																	<li
																		key={ptIndex}
																		dangerouslySetInnerHTML={{
																			__html: point.replace(
																				/\*\*(.*?)\*\*/g,
																				"<strong>$1</strong>"
																			),
																		}}
																	></li>
																) : (
																	<li key={ptIndex}>{point}</li>
																) // Render JSX directly
														)}
													</ul>
												</div>
										  ))
										: null}
									{section.note && (
										<p className="text-xs text-gray-500 mt-3">{section.note}</p>
									)}
								</div>
							</motion.div>
						))}
					</div>
				</section>
				{/* --- Contact Block --- */}
				<ContactBlock
					title="Shipping Questions?"
					description="If you have questions about shipping to your location or tracking your order, please get in touch."
				/>
			</main>
		</div>
	);
};

export default ShippingPolicyScreen;
